/**
 * 상세 테이블 공통 유틸리티 함수들
 */

import { DetailBaseDto } from "src/types/detail-table";


/**
 * 품목코드 중복 체크 (제네릭)
 * @param list 상세 데이터 목록
 * @param itemCd 체크할 품목코드
 * @param valueIndex 현재 행의 valueIndex
 * @returns 중복 여부
 */
export function isDuplicateItemCd<T extends DetailBaseDto>(
	list: T[], 
	itemCd: string, 
	valueIndex: number
): boolean {
	return list.filter(
		item => item.itemCd?.trim().toUpperCase() === itemCd.trim().toUpperCase() 
			&& item.valueIndex !== valueIndex
	).length > 0;
}

/**
 * 점검코드 중복 체크 (EQM2010용)
 * @param list 상세 데이터 목록
 * @param inspectCd 체크할 점검코드
 * @param valueIndex 현재 행의 valueIndex
 * @returns 중복 여부
 */
export function isDuplicateInspectCd<T extends DetailBaseDto & { inspectCd?: string }>(
	list: T[], 
	inspectCd: string, 
	valueIndex: number
): boolean {
	return list.filter(
		item => item.inspectCd?.trim().toUpperCase() === inspectCd.trim().toUpperCase() 
			&& item.valueIndex !== valueIndex
	).length > 0;
}

/**
 * 거래처코드 중복 체크
 */
export function isDuplicateCustomCd<T extends { customCd?: string; valueIndex: number }>(
	list: T[],
	customCd: string,
	valueIndex: number,
): boolean {
	return list.filter(
		item => item.customCd?.trim().toUpperCase() === customCd.trim().toUpperCase()
			&& item.valueIndex !== valueIndex,
	).length > 0;
}



/**
 * 모듈별 설정 생성 도우미 (단순 입력 및 검색할때만 사용)
 */
export const createModuleConfig = {

	EQM2010: () => ({
		fieldConfig: {
			qtyField: 'inspectCycle', // 점검주기를 수량 필드로 사용
			priceField: 'remark', // 비고를 가격 필드로 사용 (실제로는 사용 안함)
			unitField: 'remark', // 비고를 단위 필드로 사용 (실제로는 사용 안함)
			additionalFields: {
				inspectMethod: 'inspectMethod',
				inspectType: 'inspectType',
				inspectValue: 'inspectValue',
			},
			codeSearchFields: {
				inspectCd: {
					codeField: 'inspectCd',
					category: '설비점검코드',
					responseMapping: {
						inspectCd: 'inspectCd',
						inspectNm: 'inspectNm',
						inspectMethod: 'inspectMethod',
						inspectType: 'inspectType',
						inspectValue: 'inspectValue',
						remark: 'remark',
					},
					clearFields: {
						inspectCd: "",
						inspectNm: "",
						inspectMethod: "",
						inspectType: "",
						inspectValue: "",
						remark: ""
					},
					duplicateCheck: isDuplicateInspectCd,
				}
			}
		},
		codeSearchConfig: {
			formName: 'EQM2010_R01',
			focusMoveConfig: {
				nextColumnKey: 'inspectCycle',
				delay: 0
			}
		}
	}),

	MST3020: () => ({
		fieldConfig: {
			qtyField: 'boxQty',
			priceField: 'cycletime',
			unitField: 'remark',
			additionalFields: {
				workplaceCd: 'workplaceCd',
				workplaceNm: 'workplaceNm',
				warehouseCd: 'warehouseCd',
				warehouseNm: 'warehouseNm',
				cycletime: 'cycletime',
				cavity: 'cavity',
				boxQty: 'boxQty',
			},
			codeSearchFields: {
				workplaceCd: {
					codeField: 'workplaceCd',
					category: '공정코드',
					responseMapping: {
						workplaceCd: 'workplaceCd',
						workplaceNm: 'workplaceNm',
					},
					clearFields: {
						workplaceCd: "",
						workplaceNm: "",
					},
				},
				warehouseCd: {
					codeField: 'warehouseCd',
					category: '창고코드',
					responseMapping: {
						warehouseCd: 'warehouseCd',
						warehouseNm: 'warehouseNm',
					},
					clearFields: {
						warehouseCd: "",
						warehouseNm: "",
					},
				}
			}
		},
		codeSearchConfig: {
			formName: 'MST3020_R01',
			focusMoveConfig: {
				nextColumnKey: 'cycletime',
				delay: 0
			}
		}
	}),
	//MST3040: () => ({
	//	fieldConfig: {
	//		qtyField: '',
	//		priceField: '',
	//		unitField: 'remark',
	//		additionalFields: {
	//			coordinateCd: 'coordinateCd',
	//			coordinateNm: 'coordinateNm',
	//			xcoordinate: 'xcoordinate',
	//			ycoordinate: 'ycoordinate',
	//			orient: 'orient',
	//			topBottom: 'topBottom',
	//			remark: 'remark',
	//		},
	//		codeSearchFields: {
	//		}
	//	},
	//	codeSearchConfig: {
	//		formName: 'MST3040_R01',
	//		focusMoveConfig: {
	//			nextColumnKey: 'coordinateCd',
	//			delay: 0
	//		}
	//	}
	//}),
	MST3050: () => ({
		fieldConfig: {
			qtyField: '',
			priceField: '',
			unitField: 'itemUnit',
			additionalFields: {
				childItemcd: 'childItemcd',
				childItemNm: 'childItemNm',
				childItemSpec: 'childItemSpec',
				childItemUnit: 'childItemUnit',
				childItemUsage: 'childItemUsage',
			},
			codeSearchFields: {
				childItemcd: {
					codeField: 'itemCd',
					category: '품목',
					responseMapping: {
						itemCd : 'childItemcd',
						itemNm : 'childItemNm',
						itemSpec : 'childItemSpec',
						itemUnit : 'childItemUnit',
					},
					clearFields: {
						childItemcd: "",
						childItemNm: "",
						childItemSpec: "",
						childItemUnit: "",
					},
					duplicateCheck: isDuplicateItemCd,
					// searchFunction은 모듈별 훅에서 주입
				}
			}
		},
		codeSearchConfig: {
			formName: 'MST3050_R01',
			focusMoveConfig: {
				nextColumnKey: 'childItemcd',
				delay: 0
			}
		}
	}),
	/**
	 * SAL1010 설정
	 * salQty → orderQty: 수주단위량을 수량에 자동 입력
	 */
	SAL1010: () => ({
		fieldConfig: {
			qtyField: 'orderQty',
			priceField: 'orderPrice',
			unitField: 'orderUnit',
			additionalFields: {
				itemUnit: 'itemUnit',
			},
			codeSearchFields: {
				itemCd: {
					codeField: 'itemCd',
					category: '품목',
					responseMapping: {
						itemCd: 'itemCd',
						itemNm: 'itemNm',
						itemSpec: 'itemSpec',
						itemUnit: 'orderUnit',
						itemCost: 'orderPrice',
						salQty: 'orderQty',
					},
					clearFields: {
						itemCd: "",
						itemNm: "",
						itemSpec: "",
						orderUnit: "",
						orderPrice: 0,
						orderQty: 0,
						dcAmt: 0,
						itemAmt: 0
					},
					duplicateCheck: isDuplicateItemCd,
					// searchFunction은 모듈별 훅에서 주입
				}
			}
		},
		codeSearchConfig: {
			formName: 'SAL1010_R01',
			focusMoveConfig: {
				nextColumnKey: 'orderQty',
				delay: 100
			}
		}
	}),

	/**
	 * SAL2020 설정
	 */
	SAL2020: () => ({
		fieldConfig: {
            qtyField: "salesQty",
            priceField: "salesPrice",
            dcField: "dcAmt",
            amountField: "itemAmt",
			unitField: 'unit',
			additionalFields: {
				modelNo: 'modelNo',
				itemCost: 'itemCost',
			},
			codeSearchFields: {
				itemCd: {
					codeField: 'itemCd',
					category: '품목',
					responseMapping: {
						itemCd: 'itemCd',
						itemNm: 'itemNm',
						itemSpec: 'itemSpec',
						modelNo: 'modelNo', 
						itemUnit: 'unit',
						itemCost: 'salesPrice'
					},
					clearFields: {
						itemCd: "",
						itemNm: "",
						itemSpec: "",
						modelNo: "",
						unit: "",
						salesPrice: 0,
						dcAmt: 0,
						itemAmt: 0
					},
					duplicateCheck: isDuplicateItemCd,
					// searchFunction은 모듈별 훅에서 주입
				}
			}
		},
		codeSearchConfig: {
			formName: 'SAL2020_R01',
			useLegacyFocus: true
		}
	}),

	/**
	 * PUR1030 설정 (구매주문)
	 * purQty → orderQty: 발주단위량을 수량에 자동 입력
	 */
	PUR1030: () => ({
		fieldConfig: {
			qtyField: 'orderQty',
			priceField: 'orderPrice',
			unitField: 'orderUnit',
			additionalFields: {
				modelNo: 'modelNo',
				itemCost: 'itemCost',
			},
			codeSearchFields: {
				itemCd: {
					codeField: 'itemCd',
					category: '품목',
					responseMapping: {
						itemCd: 'itemCd',
						itemNm: 'itemNm',
						itemSpec: 'itemSpec',
						modelNo: 'modelNo',
						itemUnit: 'orderUnit',
						itemCost: 'orderPrice',
						purQty: 'orderQty',
						locationCd: 'locationCd',
						locationNm: 'locationNm',
					},
					clearFields: {
						itemCd: "",
						itemNm: "",
						itemSpec: "",
						modelNo: "",
						orderUnit: "",
						orderPrice: 0,
						orderQty: 0,
						dcAmt: 0,
						itemAmt: 0,
						locationCd: "",
						locationNm: "",
					},
					duplicateCheck: isDuplicateItemCd,
					// searchFunction은 모듈별 훅에서 주입
				}
			}
		},
		codeSearchConfig: {
			formName: 'PUR1030_R01',
			focusMoveConfig: {
				nextColumnKey: 'orderQty',
				delay: 100
			}
		}
	}),

	/**
	 * PUR2010 설정 (구매실적)
	 */
	PUR2010: () => ({
		fieldConfig: {
			qtyField: 'billQty',
			priceField: 'billPrice',
			unitField: 'billUnit',
			additionalFields: {
				modelNo: 'modelNo',
				itemCost: 'itemCost',
			},
			codeSearchFields: {
				itemCd: {
					codeField: 'itemCd',
					category: '품목',
					responseMapping: {
						itemCd: 'itemCd',
						itemNm: 'itemNm',
						itemSpec: 'itemSpec',
						modelNo: 'modelNo',
						itemUnit: 'billUnit',
						itemCost: 'billPrice',
						locationCd: 'locationCd',
						locationNm: 'locationNm',
					},
					clearFields: {
						itemCd: "",
						itemNm: "",
						itemSpec: "",
						modelNo: "",
						billUnit: "",
						billPrice: 0,
						dcAmt: 0,
						billAmt: 0,
						locationCd: "",
						locationNm: "",
					},
					duplicateCheck: isDuplicateItemCd,
					// searchFunction은 모듈별 훅에서 주입
				}
			}
		},
		codeSearchConfig: {
			formName: 'PUR2010_R01',
			focusMoveConfig: {
				nextColumnKey: 'billQty',
				delay: 100
			}
		}
	}),

	/**
	 * WMS1020 설정
	 */
	WMS1020: () => ({
		fieldConfig: {
			qtyField: 'orderQty',
			priceField: 'orderPrice',
			unitField: 'orderUnit',
			additionalFields: {
				itemUnit: 'itemUnit',
			},
			codeSearchFields: {
				itemCd: {
					codeField: 'itemCd',
					category: '품목',
					responseMapping: {
						itemCd: 'itemCd',
						itemNm: 'itemNm',
						itemSpec: 'itemSpec',
						itemUnit: 'orderUnit'
					},
					clearFields: {
						itemCd: "",
						itemNm: "",
						itemSpec: "",
						orderUnit: ""
					},
					duplicateCheck: isDuplicateItemCd,
					// searchFunction은 모듈별 훅에서 주입
				}
			}
		},
		codeSearchConfig: {
			formName: 'WMS1020_R01',
			focusMoveConfig: {
				nextColumnKey: 'orderQty',
				delay: 0
			}
		}
	}),

	/**
	 * WMS1021 설정
	 */
	WMS1021: () => ({
		fieldConfig: {
			qtyField: 'orderQty',
			priceField: 'orderPrice',
			unitField: 'orderUnit',
			additionalFields: {
				itemUnit: 'itemUnit',
			},
			codeSearchFields: {
				itemCd: {
					codeField: 'itemCd',
					category: '품목',
					responseMapping: {
						itemCd: 'itemCd',
						itemNm: 'itemNm',
						itemSpec: 'itemSpec',
						itemUnit: 'orderUnit'
					},
					clearFields: {
						itemCd: "",
						itemNm: "",
						itemSpec: "",
						orderUnit: ""
					},
					duplicateCheck: isDuplicateItemCd,
					// searchFunction은 모듈별 훅에서 주입
				}
			}
		},
		codeSearchConfig: {
			formName: 'WMS1021_R01',
			focusMoveConfig: {
				nextColumnKey: 'orderQty',
				delay: 0
			}
		}
	}),

	/**
	 * WMS1022 설정
	 */
	WMS1022: () => ({
		fieldConfig: {
			qtyField: 'orderQty',
			priceField: 'orderPrice',
			unitField: 'orderUnit',
			additionalFields: {
				itemUnit: 'itemUnit',
			},
			codeSearchFields: {
				itemCd: {
					codeField: 'itemCd',
					category: '품목',
					responseMapping: {
						itemCd: 'itemCd',
						itemNm: 'itemNm',
						itemSpec: 'itemSpec',
						itemUnit: 'orderUnit'
					},
					clearFields: {
						itemCd: "",
						itemNm: "",
						itemSpec: "",
						orderUnit: ""
					},
					duplicateCheck: isDuplicateItemCd,
					// searchFunction은 모듈별 훅에서 주입
				}
			}
		},
		codeSearchConfig: {
			formName: 'WMS1022_R01',
			focusMoveConfig: {
				nextColumnKey: 'orderQty',
				delay: 0
			}
		}
	}),

	/**
	 * WMS1130 설정 (자재입고 등록)
	 * purQty → qty: 발주단위량을 입고량에 자동 입력
	 */
	WMS1130: () => ({
		fieldConfig: {
			qtyField: 'qty',
			priceField: 'cost',
			unitField: 'unit',
			additionalFields: {

			},
			codeSearchFields: {
				itemCd: {
					codeField: 'itemCd',
					category: '품목',
					responseMapping: {
						itemCd: 'itemCd',
						itemNm: 'itemNm',
						itemSpec: 'itemSpec',
						itemUnit: 'unit',
						purQty: 'qty',
						locationCd: 'locationCd',
						locationNm: 'locationNm',
					},
					clearFields: {
						itemCd: "",
						itemNm: "",
						itemSpec: "",
						unit: "",
						qty: 0,
						locationCd: "",
						locationNm: "",
					},
					duplicateCheck: () => false,
					// searchFunction은 모듈별 훅에서 주입
				}
			}
		},
		codeSearchConfig: {
			formName: 'WMS1130_R01',
			focusMoveConfig: {
				nextColumnKey: 'qty',
				delay: 100
			}
		}
	}),

	/**
	 * WMS1131 설정 (자재입고 등록)
	 * purQty → qty: 발주단위량을 입고량에 자동 입력
	 */
	WMS1131: () => ({
		fieldConfig: {
			qtyField: 'qty',
			priceField: 'cost',
			unitField: 'unit',
			additionalFields: {

			},
			codeSearchFields: {
				itemCd: {
					codeField: 'itemCd',
					category: '품목',
					responseMapping: {
						itemCd: 'itemCd',
						itemNm: 'itemNm',
						itemSpec: 'itemSpec',
						itemUnit: 'unit',
						purQty: 'qty',
						locationCd: 'locationCd',
						locationNm: 'locationNm',
					},
					clearFields: {
						itemCd: "",
						itemNm: "",
						itemSpec: "",
						unit: "",
						qty: 0,
						locationCd: "",
						locationNm: "",
					},
					duplicateCheck: () => false,
					// searchFunction은 모듈별 훅에서 주입
				}
			}
		},
		codeSearchConfig: {
			formName: 'WMS1131_R01',
			focusMoveConfig: {
				nextColumnKey: 'qty',
				delay: 100
			}
		}
	}),

	/**
	 * WMS1140 설정 (자재입고 등록)
	 * purQty → qty: 발주단위량을 입고량에 자동 입력
	 */
	WMS1140: () => ({
		fieldConfig: {
			qtyField: 'qty',
			priceField: 'cost',
			unitField: 'unit',
			additionalFields: {

			},
			codeSearchFields: {
				itemCd: {
					codeField: 'itemCd',
					category: '품목',
					responseMapping: {
						itemCd: 'itemCd',
						itemNm: 'itemNm',
						itemSpec: 'itemSpec',
						itemUnit: 'unit',
						purQty: 'qty',
						itemCost: 'cost',
						locationCd: 'locationCd',
						locationNm: 'locationNm',
					},
					clearFields: {
						itemCd: "",
						itemNm: "",
						itemSpec: "",
						unit: "",
						qty: 0,
						cost: 0,
						locationCd: "",
						locationNm: "",
					},
					duplicateCheck: () => false,
					// searchFunction은 모듈별 훅에서 주입
				}
			}
		},
		codeSearchConfig: {
			formName: 'WMS1140_R01',
			focusMoveConfig: {
				nextColumnKey: 'qty',
				delay: 100
			}
		}
	}),

	/**
	 * WMS1141 설정 (자재입고 등록)
	 * purQty → qty: 발주단위량을 입고량에 자동 입력
	 */
	WMS1141: () => ({
		fieldConfig: {
			qtyField: 'qty',
			priceField: 'cost',
			unitField: 'unit',
			additionalFields: {

			},
			codeSearchFields: {
				itemCd: {
					codeField: 'itemCd',
					category: '품목',
					responseMapping: {
						itemCd: 'itemCd',
						itemNm: 'itemNm',
						itemSpec: 'itemSpec',
						itemUnit: 'unit',
						purQty: 'qty',
						itemCost: 'cost',
						locationCd: 'locationCd',
						locationNm: 'locationNm',
					},
					clearFields: {
						itemCd: "",
						itemNm: "",
						itemSpec: "",
						unit: "",
						qty: 0,
						cost: 0,
						locationCd: "",
						locationNm: "",
					},
					duplicateCheck: () => false,
					// searchFunction은 모듈별 훅에서 주입
				}
			}
		},
		codeSearchConfig: {
			formName: 'WMS1141_R01',
			focusMoveConfig: {
				nextColumnKey: 'qty',
				delay: 100
			}
		}
	}),

	/**
	 * SAL6010 설정
	 */
	SAL6010: () => ({
		fieldConfig: {
			qtyField: 'estimateQty',
			priceField: 'estimatePrice',
			unitField: 'estimateUnit',
			additionalFields: {
				modelNo: 'modelNo',
				itemUnit: 'estimateUnit',
			},
			codeSearchFields: {
				itemCd: {
					codeField: 'itemCd',
					category: '품목',
					responseMapping: {
						itemCd: 'itemCd',
						itemNm: 'itemNm',
						modelNo: 'modelNo',
						itemSpec: 'itemSpec',
						itemUnit: 'estimateUnit',
						itemCost: 'estimatePrice',
					},
					clearFields: {
						itemCd: "",
						itemNm: "",
						itemSpec: "",
						modelNo: "",
						estimateUnit: "",
						estimatePrice: 0,
						dcAmt: 0,
						itemAmt: 0
					},
					duplicateCheck: isDuplicateItemCd,
				}
			}
		},
		codeSearchConfig: {
			formName: 'SAL6010_R01',
			focusMoveConfig: {
				nextColumnKey: 'estimateQty',
				delay: 0
			}
		}
	}),

	/**
	 * MST1060 설정 (바코드 관리)
	 */
	MST1060: () => ({
		fieldConfig: {
			qtyField: 'packQty',
			priceField: '',
			unitField: 'itemUnit',
			additionalFields: {
				itemNm: 'itemNm',
				itemSpec: 'itemSpec',
			},
			codeSearchFields: {
				itemCd: {
					codeField: 'itemCd',
					category: '품목',
					responseMapping: {
						itemCd: 'itemCd',
						itemNm: 'itemNm',
						itemSpec: 'itemSpec',
						itemUnit: 'itemUnit',
					},
					clearFields: {
						itemCd: "",
						itemNm: "",
						itemSpec: "",
						itemUnit: "EA"
					},
					nextFocusCell: 'packQty',
				}
			}
		},
		codeSearchConfig: {
			formName: 'MST1060_R10',
			focusMoveConfig: {
				nextColumnKey: 'packQty',
				delay: 0
			}
		}
	}),

	/**
	 * MST2020 설정 (거래처별 기초잔액)
	 */
	MST2020: () => ({
		fieldConfig: {
			qtyField: '',
			priceField: '',
			unitField: '',
			additionalFields: {},
			codeSearchFields: {
				customCd: {
					codeField: 'customCd',
					category: '거래처',
					responseMapping: {
						customCd: 'customCd',
						customNm: 'customNm',
					},
					clearFields: {
						customCd: '',
						customNm: '',
					},
					duplicateCheck: isDuplicateCustomCd,
				},
			},
		},
		codeSearchConfig: {
			formName: 'MST2020_R01',
			focusMoveConfig: {
				nextColumnKey: 'purchaseAmt',
				delay: 100,
			},
		},
	}),

	EQM3020: () => ({
		fieldConfig: {
			qtyField: '',
			priceField: '',
			unitField: '',
			additionalFields: { empNm: 'empNm' },
			codeSearchFields: {
				empNo: {
					codeField: 'empNo',
					category: '담당자',
					responseMapping: { empNo: 'empNo', empNm: 'empNm' },
					clearFields: { empNo: '', empNm: '' },
				},
			},
		},
		codeSearchConfig: {
			formName: 'EQM3020_R01',
			focusMoveConfig: { nextColumnKey: 'passYn', delay: 100 },
		},
	}),

};
